import React, { useState, useEffect } from "react";
import { Layout, Menu, Avatar, Dropdown, Typography, Badge, Modal, Button, Space } from "antd";
import {
  DashboardOutlined,
  TeamOutlined,
  GiftOutlined,
  SettingOutlined,
  GlobalOutlined,
  LogoutOutlined,
  UserOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BellOutlined,
  TrophyOutlined,
  TransactionOutlined,
  InfoCircleOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { Switch, Route, Redirect, useHistory, useLocation } from "react-router-dom";
import EnterpriseLoyaltyAPI from "../../../api/EnterpriseLoyalty";
import "./EnterpriseDashboard.scss";

// Import dashboard views (we'll create these next)
const AnalyticsDashboard = React.lazy(() => import("../Views/AnalyticsDashboard"));
const CustomerManagement = React.lazy(() => import("../Views/CustomerManagement"));
const LoyaltyProgramManagement = React.lazy(() => import("../Views/LoyaltyProgramManagement"));
const RewardsManagement = React.lazy(() => import("../Views/RewardsManagement"));
const TransactionHistory = React.lazy(() => import("../Views/TransactionHistory"));
const Settings = React.lazy(() => import("../Views/Settings"));
const EngagementChannels = React.lazy(() => import("../Views/EngagementChannels"));
const EngagementChannelsPreview = React.lazy(() => import("../Views/EngagementChannelsPreview"));

const { Header, Sider, Content } = Layout;
const { Text, Title } = Typography;

const EnterpriseDashboard = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [user, setUser] = useState(() => EnterpriseLoyaltyAPI.getCurrentUser());
  const [organization, setOrganization] = useState(() => EnterpriseLoyaltyAPI.getCurrentOrganization());
  const [show2FAPrompt, setShow2FAPrompt] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const isEnterpriseAdmin = Number(user?.role_id) === 1;
  const selectedMenuKey = location.pathname.startsWith("/e/channels")
    ? "/e/channels"
    : location.pathname;

  useEffect(() => {
    // Load user and organization data
    const currentUser = EnterpriseLoyaltyAPI.getCurrentUser();
    const currentOrg = EnterpriseLoyaltyAPI.getCurrentOrganization();
    
    setUser(currentUser);
    setOrganization(currentOrg);
    
    // If no user data, redirect to login
    if (!currentUser) {
      console.log("No user found, redirecting to login");
      history.push("/e/login");
      return;
    }

    // Check if 2FA is setup
    if (currentOrg && !currentOrg.two_factor_enabled) {
      setShow2FAPrompt(true);
    }
  }, [history]);

  const handleGoToSettings = () => {
    setShow2FAPrompt(false);
    history.push("/e/settings");
  };

  const handleLogout = () => {
    EnterpriseLoyaltyAPI.logout();
    history.push("/e/login");
  };

  const userMenu = (
    <Menu>
      <Menu.Item key="profile" icon={<UserOutlined />}>
        My Profile
      </Menu.Item>
      <Menu.Item key="settings" icon={<SettingOutlined />}>
        Settings
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={handleLogout}>
        Logout
      </Menu.Item>
    </Menu>
  );

  const menuItems = [
    {
      key: "/e/dashboard",
      icon: <DashboardOutlined />,
      label: "Analytics Dashboard",
    },
    {
      key: "/e/customers",
      icon: <TeamOutlined />,
      label: "Member Management",
    },
    {
      key: "/e/loyalty-programs",
      icon: <TrophyOutlined />,
      label: "Loyalty Programs & Tiers",
    },
    {
      key: "/e/transactions",
      icon: <TransactionOutlined />,
      label: "Transaction History",
    },
    {
      key: "/e/rewards",
      icon: <GiftOutlined />,
      label: "Rewards & Redemptions",
    },
    {
      key: "/e/settings",
      icon: <SettingOutlined />,
      label: "Settings",
    },
    isEnterpriseAdmin
      ? {
          key: "/e/channels",
          icon: <GlobalOutlined />,
          label: "Channels",
        }
      : null,
  ].filter(Boolean);

  const handleMenuClick = ({ key }) => {
    history.push(key);
  };

  return (
    <Layout className="enterprise-dashboard-layout">
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={260}
        className="dashboard-sider"
      >
        <div className="sider-header">
          <img
            src="/loystar-favicon-png.png"
            alt="Loystar"
            className="sider-logo"
          />
          {!collapsed && (
            <Text className="sider-title">Enterprise Suite</Text>
          )}
        </div>

        <Menu
          mode="inline"
          selectedKeys={[selectedMenuKey]}
          items={menuItems}
          onClick={handleMenuClick}
          className="dashboard-menu"
        />

        {!collapsed && organization && (
          <div className="sider-footer">
          </div>
        )}
      </Sider>

      <Layout className="site-layout">
        <Header className="dashboard-header">
          <div className="header-left">
            {React.createElement(
              collapsed ? MenuUnfoldOutlined : MenuFoldOutlined,
              {
                className: "trigger",
                onClick: () => setCollapsed(!collapsed),
              }
            )}
            {organization && (
              <Text className="organization-name">{organization.name}</Text>
            )}
          </div>

          <div className="header-right">
            <Badge count={0} className="notification-badge">
              <BellOutlined className="header-icon" />
            </Badge>

            <Dropdown overlay={userMenu} placement="bottomRight" trigger={["click"]}>
              <div className="user-profile">
                <Avatar size={36} icon={<UserOutlined />} className="user-avatar" />
                <div className="user-info">
                  <Text className="user-name">{user?.name || "Admin"}</Text>
                  <Text className="user-role">{user?.role || "Administrator"}</Text>
                </div>
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content className="dashboard-content">
          <React.Suspense fallback={<div className="loading-container">Loading...</div>}>
            <Switch>
              <Route exact path="/e/dashboard" component={AnalyticsDashboard} />
              <Route path="/e/customers" component={CustomerManagement} />
              <Route path="/e/loyalty-programs" component={LoyaltyProgramManagement} />
              <Route path="/e/transactions" component={TransactionHistory} />
              <Route path="/e/rewards" component={RewardsManagement} />
              <Route path="/e/settings" component={Settings} />
              <Route
                path="/e/channels/preview"
                render={() =>
                  isEnterpriseAdmin ? <EngagementChannelsPreview /> : <Redirect to="/e/dashboard" />
                }
              />
              <Route
                exact
                path="/e/channels"
                render={() =>
                  isEnterpriseAdmin ? <EngagementChannels /> : <Redirect to="/e/dashboard" />
                }
              />
              <Route path="/e/*">
                <div>404 - Page Not Found</div>
              </Route>
            </Switch>
          </React.Suspense>
        </Content>
      </Layout>
      <Modal
        title="Secure Your Account"
        open={show2FAPrompt}
        onCancel={() => setShow2FAPrompt(false)}
        footer={[
          <Button key="skip" onClick={() => setShow2FAPrompt(false)}>
            Skip for now
          </Button>,
          <Button key="setup" type="primary" icon={<LockOutlined />} onClick={handleGoToSettings}>
            Set up 2FA Now
          </Button>
        ]}
        centered
      >
        <div style={{ textAlign: "center", padding: "10px 0" }}>
          <div style={{ fontSize: "48px", color: "#faad14", marginBottom: "16px" }}>
            <InfoCircleOutlined />
          </div>
          <Title level={4}>Enable Two-Factor Authentication</Title>
          <Text>
            Your organization doesn't have Two-Factor Authentication (2FA) enabled yet. 
            Protect your sensitive loyalty data by adding an extra layer of security.
          </Text>
        </div>
      </Modal>
    </Layout>
  );
};

export default EnterpriseDashboard;
